import { keepPreviousData, queryOptions, useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { useAuthStore } from "@/lib/auth-store";
import { autorizarLote, listarBandejaAutorizacion } from "../services/bandeja-service";
import { toastErrorApi } from "@/lib/toast";

/**
 * Polling de 5s · suficiente para que un autorizador note una nueva solicitud sin
 * pegarle al server cada segundo. El loader del route precarga el primer fetch.
 *
 * Los filtros de columna y el paginado van al server como query params y forman
 * parte del queryKey (refetch al cambiar). Usamos `useQuery` + `keepPreviousData`
 * (en vez de `useSuspenseQuery`) para que, al cambiar el filtro o la página, NO
 * re-suspenda y parpadee: se mantienen las filas previas mientras llega el set.
 */
const REFETCH_INTERVAL_MS = 5_000;

/**
 * Sin `staleTime`, cada montaje considera vieja la foto cacheada y dispara un
 * refetch inmediato (encima del que ya entrega el loader), por lo que al entrar
 * se pintaba el snapshot anterior y un instante después se corregía —el "parpadeo"
 * de filas que aparecen y desaparecen en una cola que se consume sola. Con un
 * `staleTime` justo por debajo del intervalo de polling, re-entrar rápido reusa la
 * foto reciente sin refetch extra; el polling de 5s mantiene la frescura.
 */
const STALE_TIME_MS = 4_000;

/** Tamaño de página compartido entre la page y el loader del route (misma queryKey). */
export const BANDEJA_PAGE_SIZE = 15;

export const bandejaAutorizacionQueryOptions = (
  params: Record<string, string | number> = { page: 1, pageSize: BANDEJA_PAGE_SIZE },
) => {
  // Segmentamos el caché por usuario: la bandeja depende del rol/organización del
  // viewer. Sin esto, cerrar sesión y entrar con otra cuenta en la misma pestaña
  // mostraba primero las filas del usuario anterior hasta que llegaba el refetch.
  // (El logout además hace `queryClient.clear()`; esto es defensa en profundidad.)
  const usuarioId = useAuthStore.getState().user?.id ?? "anon";
  return queryOptions({
    queryKey: [...qk.bandejaAutorizacion, usuarioId, params] as const,
    queryFn: () => listarBandejaAutorizacion(params),
    refetchInterval: REFETCH_INTERVAL_MS,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME_MS,
  });
};

export const useBandejaAutorizacion = (
  params: Record<string, string | number> = { page: 1, pageSize: BANDEJA_PAGE_SIZE },
) => useQuery(bandejaAutorizacionQueryOptions(params));

/**
 * Autorización MASIVA · autoriza el nivel actual de las solicitudes seleccionadas.
 * Al terminar invalida la bandeja, el dashboard y las listas de solicitudes para
 * reflejar el avance. El resumen (autorizadas/fallidas) lo maneja el llamador.
 */
export function useAutorizarLote(opts?: { onExito?: (r: Awaited<ReturnType<typeof autorizarLote>>) => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, fechaPago }: { ids: string[]; fechaPago?: string | null }) =>
      autorizarLote(ids, fechaPago),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.bandejaAutorizacion });
      void qc.invalidateQueries({ queryKey: qk.misSolicitudes });
      void qc.invalidateQueries({ queryKey: qk.misEstadisticas });
      opts?.onExito?.(r);
    },
    onError: toastErrorApi,
  });
}
